import React, { useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts";
import dayjs from "dayjs";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const formatUtcToBrowserDate = (time) => {
  if (!time) return null;

  const normalizedTime =
    typeof time === "string" && !time.endsWith("Z") ? `${time}Z` : time;

  return dayjs(normalizedTime);
};

const WiredLinkDegradationDrawerChart = ({ data = [] }) => {
  const theme = useTheme();
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const chartData = useMemo(() => {
    const series = Array.isArray(data) ? data : (data?.series ?? []);
    const dataMap = new Map();

    series.forEach((item) => {
      if (!Array.isArray(item)) return;

      const [time, value] = item;
      const browserTime = formatUtcToBrowserDate(time);

      if (!browserTime?.isValid()) return;

      const key = browserTime.format("YYYY-MM-DD HH");

      dataMap.set(key, {
        healthy: Number(value?.healthy) || 0,
        degraded: Number(value?.degraded) || 0,
        down: Number(value?.down) || 0,
      });
    });

    const lastTime =
      series.length > 0
        ? formatUtcToBrowserDate(series[series.length - 1]?.[0])
        : dayjs().startOf("hour");

    const endTime =
      lastTime?.isValid() === true
        ? lastTime.startOf("hour")
        : dayjs().startOf("hour");

    const startTime = endTime.subtract(23, "hour");

    return Array.from({ length: 24 }, (_, index) => {
      const currentTime = startTime.add(index, "hour");
      const key = currentTime.format("YYYY-MM-DD HH");
      const label = currentTime.format("HH:mm");
      const item = dataMap.get(key);

      return {
        label,
        healthy: item?.healthy ?? 0,
        degraded: item?.degraded ?? 0,
        down: item?.down ?? 0,
      };
    });
  }, [data]);

  const option = useMemo(() => {
    const xAxisData = chartData.map((item) => item.label);

    const goodData = chartData.map((item) => item.healthy);
    const warningData = chartData.map((item) => item.degraded);
    const criticalData = chartData.map((item) => item.down);

    const maxValue = Math.max(
      ...chartData.map((item) => item.healthy + item.degraded + item.down),
      0,
    );

    const yAxisMax = Math.max(3, Math.ceil(maxValue));
    const yAxisInterval = yAxisMax <= 3 ? 1 : Math.ceil(yAxisMax / 3);

    return {
      animation: false,
      backgroundColor: theme.palette.primary.white,

      legend: {
        top: 0,
        left: 0,
        itemWidth: 12,
        itemHeight: 12,
        icon: "roundRect",
        selectedMode: true,
        data: ["Critical", "Warning", "Good"],
        textStyle: {
          color: theme.palette.primary.greyPrimary,
          fontSize: theme.typography.customFontSize[2],
        },
      },

      grid: {
        top: 42,
        right: 18,
        bottom: 36,
        left: 40,
        containLabel: true,
      },

      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        backgroundColor: theme.palette.primary.blackTransparency,
        borderWidth: 0,
        textStyle: {
          color: theme.palette.primary.white,
          fontSize: theme.typography.customFontSize[0],
        },
        formatter: (params) => {
          const critical =
            params.find((item) => item.seriesName === "Critical")?.data ?? 0;

          const warning =
            params.find((item) => item.seriesName === "Warning")?.data ?? 0;

          const good =
            params.find((item) => item.seriesName === "Good")?.data ?? 0;

          const time = params?.[0]?.axisValue || "";

          return `
          <div style="min-width: 50px;">
            <div style="font-size: 14px; font-weight: 600; margin-bottom: 4px;">
              ${time}
            </div>

            <div style="display: flex; align-items: center; gap: 6px; line-height: 18px;">
              <span style="width: 10px; height: 10px; background: ${theme.palette.primary.greenPrimary}; display: inline-block;"></span>
              <span>${good}</span>
            </div>

            <div style="display: flex; align-items: center; gap: 6px; line-height: 18px;">
              <span style="width: 10px; height: 10px; background: ${theme.palette.primary.orangeThird}; display: inline-block;"></span>
              <span>${warning}</span>
            </div>

            <div style="display: flex; align-items: center; gap: 6px; line-height: 18px;">
              <span style="width: 10px; height: 10px; background: ${theme.palette.primary.redPrimary}; display: inline-block;"></span>
              <span>${critical}</span>
            </div>
          </div>
        `;
        },
      },

      xAxis: {
        type: "category",
        data: xAxisData,
        boundaryGap: true,
        axisLine: {
          show: true,
          lineStyle: {
            color: theme.palette.primary.greyThread,
          },
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          color: theme.palette.primary.greyFourth,
          fontSize: theme.typography.customFontSize[0],
          interval: 0,
          formatter: (value, index) => {
            const isLast = index === xAxisData.length - 1;
            return index % 4 === 0 || isLast ? value : "";
          },
        },
        splitLine: {
          show: true,
          interval: 0,
          lineStyle: {
            color: theme.palette.primary.greyThread,
            type: "solid",
          },
        },
      },

      yAxis: {
        type: "value",
        min: 0,
        max: yAxisMax,
        interval: yAxisInterval,
        axisLabel: {
          color: theme.palette.primary.greyFourth,
          fontSize: theme.typography.customFontSize[0],
          formatter: (value) => `${Math.round(value)}`,
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: theme.palette.primary.greyThread,
            type: "dashed",
          },
        },
      },

      series: [
        {
          name: "Critical",
          type: "bar",
          stack: "total",
          data: criticalData,
          barWidth: 12,
          itemStyle: {
            color: theme.palette.primary.redPrimary,
          },
          emphasis: {
            focus: "series",
          },
        },
        {
          name: "Warning",
          type: "bar",
          stack: "total",
          data: warningData,
          barWidth: 12,
          itemStyle: {
            color: theme.palette.primary.orangeThird,
          },
          emphasis: {
            focus: "series",
          },
        },
        {
          name: "Good",
          type: "bar",
          stack: "total",
          data: goodData,
          barWidth: 12,
          itemStyle: {
            color: theme.palette.primary.greenPrimary,
          },
          emphasis: {
            focus: "series",
          },
        },
      ],
    };
  }, [chartData, theme]);

  useEffect(() => {
    if (!chartRef.current) return;

    const chart =
      echarts.getInstanceByDom(chartRef.current) ||
      echarts.init(chartRef.current);

    chartInstanceRef.current = chart;
    chart.setOption(option, true);
    chart.resize();

    const handleResize = () => {
      chart.resize();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);

      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, [option]);

  return (
    <Box sx={{ width: "100%" }}>
      <div
        ref={chartRef}
        style={{
          width: "100%",
          height: "320px",
        }}
      />
    </Box>
  );
};

export default WiredLinkDegradationDrawerChart;
